import { useState, useEffect } from 'react';
import { Prompt, PromptFilters, SearchResult, PromptHistoryEntry, RecentlyExecutedPrompt } from '../types';
import { supabase } from '../lib/supabase';
import { supabaseQuery, LoadingStateManager, createCleanupManager } from '../utils/dataLoader';

const RECENTLY_EXECUTED_LIMIT = 15;

export const usePrompts = (userId: string | null) => {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [filteredPrompts, setFilteredPrompts] = useState<Prompt[]>([]);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [recentlyExecuted, setRecentlyExecuted] = useState<RecentlyExecutedPrompt[]>([]);
  const [filters, setFilters] = useState<PromptFilters>({
    search: '',
    aiPlatform: '',
    sortBy: 'recent',
    showTemplatesOnly: false,
    showFavoritesOnly: false,
    showRecentlyExecutedOnly: false,
    folderId: undefined, // New filter for current folder
    domain: '',
    output: '',
    platforms: '',
    webPlatforms: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [loadingManager] = useState(() => new LoadingStateManager());

  useEffect(() => {
    const cleanup = createCleanupManager();
    
    if (userId) {
      // Only load data if tab is visible
      if (!document.hidden) {
        // Load data with slight delays to prevent blocking
        const timeout1 = setTimeout(() => {
          if (!document.hidden) loadPrompts();
        }, 50);
        const timeout2 = setTimeout(() => {
          if (!document.hidden) loadRecentlyExecuted();
        }, 100);
        cleanup.addTimeout(timeout1);
        cleanup.addTimeout(timeout2);
      } else {
        // Load when tab becomes visible
        const handleVisibilityChange = () => {
          if (!document.hidden && userId) {
            loadPrompts();
            loadRecentlyExecuted();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
          }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        cleanup.addTimeout(setTimeout(() => {
          document.removeEventListener('visibilitychange', handleVisibilityChange);
        }, 30000)); // Cleanup after 30 seconds
      }
    } else {
      setPrompts([]);
      setRecentlyExecuted([]);
      loadingManager.reset();
      setIsLoading(false);
    }
    
    return cleanup.cleanup;
  }, [userId]);

  useEffect(() => {
    filterAndSortPrompts();
  }, [prompts, recentlyExecuted, filters]);

  const loadPrompts = async () => {
    if (!userId) return;

    // Skip loading if tab is not visible
    if (document.hidden) {
      console.log('🔵 Prompts: Skipping load - tab not visible');
      return;
    }

    try {
      setIsLoading(loadingManager.shouldShowLoading(prompts.length));

      const promptsData = await supabaseQuery(
        () => supabase
          .from('prompts')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false }),
        'Load Prompts',
        { timeout: 15000, maxRetries: 2 }
      );

      // Load prompt history for each prompt with batching to prevent hanging
      const promptsWithHistory = await Promise.allSettled(
        (promptsData || []).map(async (promptItem) => {
          try {
            const historyData = await supabaseQuery(
              () => supabase
                .from('prompt_history')
                .select('*')
                .eq('prompt_id', promptItem.id)
                .order('created_at', { ascending: false }),
              `Load History for ${promptItem.id}`,
              { timeout: 2000, maxRetries: 1 }
            );

            return {
              ...promptItem,
              createdAt: new Date(promptItem.created_at),
              updatedAt: new Date(promptItem.updated_at),
              lastExecutedAt: promptItem.last_executed_at ? new Date(promptItem.last_executed_at) : undefined,
              userId: promptItem.user_id,
              aiPlatform: promptItem.ai_platform,
              isFavorite: promptItem.is_favorite,
              isTemplate: promptItem.is_template,
              folderId: promptItem.folder_id,
              platforms: promptItem.platforms || [],
              webPlatforms: promptItem.web_platforms || [],
              history: (historyData || []).map(h => ({
                ...h,
                timestamp: new Date(h.created_at),
              })),
            } as Prompt;
          } catch (error) {
            console.warn('Failed to load history for prompt', promptItem.id, error);
            // Return prompt without history if history fails
            return {
              ...promptItem,
              createdAt: new Date(promptItem.created_at),
              updatedAt: new Date(promptItem.updated_at),
              lastExecutedAt: promptItem.last_executed_at ? new Date(promptItem.last_executed_at) : undefined,
              userId: promptItem.user_id,
              aiPlatform: promptItem.ai_platform,
              isFavorite: promptItem.is_favorite,
              isTemplate: promptItem.is_template,
              folderId: promptItem.folder_id,
              platforms: promptItem.platforms || [],
              webPlatforms: promptItem.web_platforms || [],
              history: [],
            } as Prompt;
          }
        })
      );

      // Filter out failed promises and extract successful results
      const successfulPrompts = promptsWithHistory
        .filter(result => result.status === 'fulfilled')
        .map(result => (result as PromiseFulfilledResult<Prompt>).value);
        
      setPrompts(successfulPrompts);
      loadingManager.markDataLoaded(successfulPrompts.length);
    } catch (error) {
      loadingManager.markDataLoaded(0);
      throw error; // Let the error bubble up to be handled by the UI
    } finally {
      setIsLoading(false);
    }
  };

  const loadRecentlyExecuted = async () => {
    if (!userId) return;
    
    // Skip loading if tab is not visible
    if (document.hidden) {
      console.log('🔵 Prompts: Skipping recently executed load - tab not visible');
      return;
    }
    
    console.log('🔵 Prompts: Loading recently executed for user:', userId);

    try {
      const data = await supabaseQuery(
        () => supabase
          .from('recently_executed')
          .select('*')
          .eq('user_id', userId)
          .order('executed_at', { ascending: false })
          .limit(RECENTLY_EXECUTED_LIMIT),
        'Load Recently Executed',
        { timeout: 15000, maxRetries: 2 }
      );
      
      console.log('🔵 Prompts: Loaded', data?.length || 0, 'recently executed prompts');

      const recentlyExecutedData = (data || []).map(item => ({
        promptId: item.prompt_id,
        executedAt: new Date(item.executed_at),
        promptSnapshot: {
          ...item.prompt_snapshot,
          createdAt: new Date(item.prompt_snapshot.createdAt),
          updatedAt: new Date(item.prompt_snapshot.updatedAt),
          lastExecutedAt: item.prompt_snapshot.lastExecutedAt 
            ? new Date(item.prompt_snapshot.lastExecutedAt) 
            : undefined,
        },
      }));

      setRecentlyExecuted(recentlyExecutedData);
    } catch (error) {
      console.error('Error loading recently executed:', error);
    }
  };

  const addRecentlyExecutedPrompt = async (prompt: Prompt) => {
    if (!userId) return;

    const executedAt = new Date();

    try {
      // Update prompt's last_executed_at
      await supabase
        .from('prompts')
        .update({ last_executed_at: executedAt.toISOString() })
        .eq('id', prompt.id);

      // Add to recently executed (upsert to handle duplicates)
      await supabase
        .from('recently_executed')
        .upsert({
          user_id: userId,
          prompt_id: prompt.id,
          executed_at: executedAt.toISOString(),
          prompt_snapshot: {
            ...prompt,
            lastExecutedAt: executedAt,
          },
        }, {
          onConflict: 'user_id,prompt_id'
        });

      // Reload data
      await loadPrompts();
      await loadRecentlyExecuted();
    } catch (error) {
      console.error('Error adding recently executed prompt:', error);
    }
  };

  const addPrompt = async (promptData: Omit<Prompt, 'id' | 'createdAt' | 'updatedAt' | 'userId'>) => {
    if (!userId) return;

    try {
      const { data, error } = await supabase
        .from('prompts')
        .insert({
          user_id: userId,
          title: promptData.title,
          body: promptData.body,
          ai_platform: promptData.aiPlatform || '',
          domain: promptData.domain || '',
          output: promptData.output || '',
          platforms: promptData.platforms || [],
          web_platforms: promptData.webPlatforms || [],
          is_favorite: promptData.isFavorite || false,
          is_template: promptData.isTemplate || false,
          folder_id: promptData.folderId || null, // New field
        })
        .select()
        .single();

      if (error) {
        console.error('Error adding prompt:', error);
        return;
      }

      await loadPrompts();
    } catch (error) {
      console.error('Error adding prompt:', error);
    }
  };

  const updatePrompt = async (id: string, updates: Partial<Prompt>, historyType?: 'enhanced' | 'manual' | 'variation') => {
    if (!userId) return;

    console.log('🔵 usePrompts: updatePrompt called', {
      promptId: id,
      updates: Object.keys(updates),
      historyType,
      userId
    });
    try {
      // Get current prompt for history
      const currentPrompt = prompts.find(p => p.id === id);
      console.log('🔵 usePrompts: Current prompt found:', !!currentPrompt);
      
      // Save to history if body is changing
      if (updates.body && currentPrompt && updates.body !== currentPrompt.body) {
        console.log('🔵 usePrompts: Saving to history - body changed');
        await supabase
          .from('prompt_history')
          .insert({
            prompt_id: id,
            body: currentPrompt.body,
            type: historyType || 'manual',
            description: historyType === 'enhanced' ? 'AI Enhanced Version' : 
                        historyType === 'variation' ? 'Variation Applied' : 
                        'Manual Edit',
          });
        console.log('✅ usePrompts: History saved');
      }

      // Update prompt
      const updateData: any = {};
      if (updates.title !== undefined) updateData.title = updates.title;
      if (updates.body !== undefined) updateData.body = updates.body;
      if (updates.aiPlatform !== undefined) updateData.ai_platform = updates.aiPlatform;
      if (updates.domain !== undefined) updateData.domain = updates.domain;
      if (updates.output !== undefined) updateData.output = updates.output;
      if (updates.platforms !== undefined) updateData.platforms = updates.platforms;
      if (updates.webPlatforms !== undefined) updateData.web_platforms = updates.webPlatforms;
      if (updates.isFavorite !== undefined) updateData.is_favorite = updates.isFavorite;
      if (updates.isTemplate !== undefined) updateData.is_template = updates.isTemplate;
      if (updates.folderId !== undefined) updateData.folder_id = updates.folderId; // New field
      if (updates.lastExecutedAt !== undefined) updateData.last_executed_at = updates.lastExecutedAt?.toISOString();

      console.log('🔵 usePrompts: Updating prompt in database', {
        promptId: id,
        updateData: Object.keys(updateData)
      });
      const { error } = await supabase
        .from('prompts')
        .update(updateData)
        .eq('id', id);

      if (error) {
        console.error('🔴 usePrompts: Database update failed:', error);
        throw new Error(`Database update failed: ${error.message}`);
      }

      console.log('✅ usePrompts: Database update successful, reloading prompts...');
      await loadPrompts();
      console.log('✅ usePrompts: Prompts reloaded');
    } catch (error) {
      console.error('🔴 usePrompts: updatePrompt failed:', error);
      throw error; // Re-throw so calling code can handle it
    }
  };

  const batchUpdatePrompts = async (promptIds: string[], updates: Partial<Prompt>) => {
    if (!userId) return [];

    try {
      const updateData: any = {};
      if (updates.domain !== undefined) updateData.domain = updates.domain;
      if (updates.output !== undefined) updateData.output = updates.output;
      if (updates.aiPlatform !== undefined) updateData.ai_platform = updates.aiPlatform;
      if (updates.platforms !== undefined) updateData.platforms = updates.platforms;
      if (updates.webPlatforms !== undefined) updateData.web_platforms = updates.webPlatforms;
      if (updates.isFavorite !== undefined) updateData.is_favorite = updates.isFavorite;
      if (updates.isTemplate !== undefined) updateData.is_template = updates.isTemplate;
      if (updates.folderId !== undefined) updateData.folder_id = updates.folderId; // New field

      const { error } = await supabase
        .from('prompts')
        .update(updateData)
        .in('id', promptIds);

      if (error) {
        console.error('Error batch updating prompts:', error);
        return [];
      }

      await loadPrompts();
      return prompts.filter(p => promptIds.includes(p.id));
    } catch (error) {
      console.error('Error batch updating prompts:', error);
      return [];
    }
  };

  const deletePrompt = async (id: string) => {
    if (!userId) return false;

    try {
      const promptToDelete = prompts.find(p => p.id === id);
      if (!promptToDelete) return false;

      // Delete from database
      const { error } = await supabase
        .from('prompts')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting prompt:', error);
        return false;
      }

      // Let the Dashboard component handle the UI update through the pendingDeletions state
      return true;
    } catch (error) {
      console.error('Error deleting prompt:', error);
      return false;
    }
  };

  const toggleFavorite = async (id: string) => {
    const prompt = prompts.find(p => p.id === id);
    if (prompt) {
      await updatePrompt(id, { isFavorite: !prompt.isFavorite });
    }
  };

  const movePromptsToFolder = async (promptIds: string[], folderId: string | null) => {
    await batchUpdatePrompts(promptIds, { folderId });
  };

  const getAllPlatforms = () => {
    const platformSet = new Set<string>();
    prompts.forEach(prompt => {
      if (prompt.aiPlatform) platformSet.add(prompt.aiPlatform);
      if (prompt.platforms) {
        prompt.platforms.forEach(platform => platformSet.add(platform));
      }
      if (prompt.webPlatforms) {
        prompt.webPlatforms.forEach(platform => platformSet.add(platform));
      }
    });
    return Array.from(platformSet).sort();
  };

  const getAllDomains = () => {
    const domainSet = new Set<string>();
    prompts.forEach(prompt => {
      if (prompt.domain) domainSet.add(prompt.domain);
    });
    return Array.from(domainSet).sort();
  };

  const getAllOutputs = () => {
    const outputSet = new Set<string>();
    prompts.forEach(prompt => {
      if (prompt.output) outputSet.add(prompt.output);
    });
    return Array.from(outputSet).sort();
  };

  const getAllWebPlatforms = () => {
    const webPlatformSet = new Set<string>();
    prompts.forEach(prompt => {
      if (prompt.webPlatforms) {
        prompt.webPlatforms.forEach(platform => webPlatformSet.add(platform));
      }
    });
    return Array.from(webPlatformSet).sort();
  };

  const highlightText = (text: string, query: string): string => {
    if (!query) return text;
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
  };

  const performSmartSearch = (query: string): SearchResult[] => {
    if (!query.trim()) return [];

    const results: SearchResult[] = [];
    const searchQuery = query.toLowerCase().trim();

    const titleMatch = query.match(/title:(.+)/i);

    prompts.forEach(prompt => {
      let matches = false;
      let matchType: 'title' | 'body' = 'body';
      let highlightedText = '';

      if (titleMatch) {
        const titleQuery = titleMatch[1].trim().toLowerCase();
        if (titleQuery && prompt.title.toLowerCase().includes(titleQuery)) {
          matches = true;
          matchType = 'title';
          highlightedText = highlightText(prompt.title, titleMatch[1].trim());
        }
      } else {
        if (prompt.title.toLowerCase().includes(searchQuery)) {
          matches = true;
          matchType = 'title';
          highlightedText = highlightText(prompt.title, query);
        } else if (prompt.body.toLowerCase().includes(searchQuery)) {
          matches = true;
          matchType = 'body';
          const bodyLower = prompt.body.toLowerCase();
          const matchIndex = bodyLower.indexOf(searchQuery);
          const start = Math.max(0, matchIndex - 50);
          const end = Math.min(prompt.body.length, matchIndex + searchQuery.length + 50);
          const snippet = prompt.body.substring(start, end);
          highlightedText = highlightText(
            (start > 0 ? '...' : '') + snippet + (end < prompt.body.length ? '...' : ''), 
            query
          );
        }
      }

      if (matches) {
        results.push({
          prompt,
          matchType,
          highlightedText,
        });
      }
    });

    return results.sort((a, b) => {
      if (a.matchType === 'title' && b.matchType !== 'title') return -1;
      if (b.matchType === 'title' && a.matchType !== 'title') return 1;
      return b.prompt.updatedAt.getTime() - a.prompt.updatedAt.getTime();
    });
  };

  const filterAndSortPrompts = () => {
    let filtered = [...prompts];

    // Filter by folder
    if (filters.folderId !== undefined) {
      filtered = filtered.filter(prompt => prompt.folderId === filters.folderId);
    }

    if (filters.showRecentlyExecutedOnly) {
      filtered = recentlyExecuted.map(re => re.promptSnapshot);
    }

    if (filters.search.trim()) {
      const results = performSmartSearch(filters.search);
      setSearchResults(results);
      
      if (filters.showRecentlyExecutedOnly) {
        const recentlyExecutedIds = new Set(recentlyExecuted.map(re => re.promptId));
        filtered = results
          .filter(r => recentlyExecutedIds.has(r.prompt.id))
          .map(r => r.prompt);
      } else {
        filtered = results.map(r => r.prompt);
      }
    } else {
      setSearchResults([]);
    }

    if (filters.showTemplatesOnly) {
      filtered = filtered.filter(prompt => prompt.isTemplate);
    }

    if (filters.showFavoritesOnly) {
      filtered = filtered.filter(prompt => prompt.isFavorite);
    }

    if (filters.aiPlatform) {
      filtered = filtered.filter(prompt => 
        prompt.aiPlatform === filters.aiPlatform ||
        (prompt.platforms && prompt.platforms.includes(filters.aiPlatform))
      );
    }

    if (filters.domain) {
      filtered = filtered.filter(prompt => prompt.domain === filters.domain);
    }

    if (filters.output) {
      filtered = filtered.filter(prompt => prompt.output === filters.output);
    }

    if (filters.platforms) {
      filtered = filtered.filter(prompt => 
        prompt.platforms && prompt.platforms.includes(filters.platforms!)
      );
    }

    if (filters.webPlatforms) {
      filtered = filtered.filter(prompt => 
        prompt.webPlatforms && prompt.webPlatforms.includes(filters.webPlatforms!)
      );
    }

    if (!filters.search.trim()) {
      if (filters.showRecentlyExecutedOnly) {
        filtered.sort((a, b) => {
          const aExecution = recentlyExecuted.find(re => re.promptId === a.id);
          const bExecution = recentlyExecuted.find(re => re.promptId === b.id);
          if (aExecution && bExecution) {
            return bExecution.executedAt.getTime() - aExecution.executedAt.getTime();
          }
          return 0;
        });
      } else {
        filtered.sort((a, b) => {
          switch (filters.sortBy) {
            case 'title':
              return a.title.localeCompare(b.title);
            case 'aiPlatform':
              return a.aiPlatform.localeCompare(b.aiPlatform);
            case 'recent':
            default:
              return b.createdAt.getTime() - a.createdAt.getTime();
          }
        });
      }
    }

    setFilteredPrompts(filtered);
  };

  return {
    prompts: filteredPrompts,
    allPrompts: prompts, // Expose all prompts for folder management
    searchResults,
    filters,
    setFilters,
    isLoading,
    addPrompt,
    updatePrompt,
    batchUpdatePrompts,
    deletePrompt,
    toggleFavorite,
    movePromptsToFolder, // New function
    getAllPlatforms,
    getAllDomains,
    getAllOutputs,
    getAllWebPlatforms,
    performSmartSearch,
    addRecentlyExecutedPrompt,
    recentlyExecuted,
  };
};